import React from 'react';

import { ReactComponent as ArrowSVG } from '@pkg/assets/floorGame/arrow.svg';
import { type CardSizes, CurrencyIcon, ValuePrefix } from '@pkg/components';
import { getCurrencyFromChain } from '@pkg/hooks';

import { type CollectionChainType, type SupportedTokens } from '@app/client';
import { ReactComponent as EthIcon } from '@app/pages/NFTDetailView/assets/icons/eth-small.svg';
import { ArrowChangeLabel } from '@app/pages/NFTDetailView/components/ArrowChangeLabel';
import { type FloorAskType } from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/NFTInfo.data';
import { formatEthValue } from '@app/pages/NFTDetailView/formatEthValue';

import { Change, Icon, Label, PercentLabel, Price, PriceGroup, PriceRow, Value, Wrapper } from './styles';

interface CardPricesProps {
    list_price?: string | null;
    last_buy?: number | null;
    last_sell?: number | null;
    cardSize?: CardSizes;
    isFloorPrice?: boolean;
    floor_price_change?: number | null;
    chain?: CollectionChainType;
    listPriceData?: FloorAskType['price'];
}

const getTokenPercentChange = (last_buy: number, last_sell: number) => {
    const percentage = ((last_buy / last_sell - 1) * 100).toFixed(1);
    return parseFloat(percentage);
};

export const CardPrices = ({
    list_price,
    last_buy,
    last_sell,
    cardSize,
    listPriceData,
    chain,
    isFloorPrice,
    floor_price_change,
}: CardPricesProps) => {
    const chainCurrency = getCurrencyFromChain(chain?.toLowerCase() as SupportedTokens);
    const nativePrice = listPriceData?.amount?.native;
    const rawPriceConverted =
        Number(listPriceData?.amount?.raw) / Math.pow(10, Number(listPriceData?.currency?.decimals));
    const value = formatEthValue(nativePrice || rawPriceConverted);
    const prefix =
        chainCurrency === 'eth' ? (
            <EthIcon />
        ) : (
            <ValuePrefix>
                <CurrencyIcon currency={chainCurrency} theme="dark" />
            </ValuePrefix>
        );
    return (
        <Wrapper $cardSize={cardSize}>
            {list_price ? (
                <PriceGroup>
                    {isFloorPrice ? (
                        <Label $cardSize={cardSize}>Floor price</Label>
                    ) : (
                        <Label $cardSize={cardSize}>List price</Label>
                    )}
                    <PriceRow>
                        <Price>
                            <Icon $cardSize={cardSize}>{prefix}</Icon>
                            <Value $cardSize={cardSize}>{value}</Value>
                            {floor_price_change && (
                                <PercentLabel $isDesc={floor_price_change < 0}>
                                    <ArrowSVG />
                                    <span>{Math.abs(floor_price_change).toFixed(1)}%</span>
                                </PercentLabel>
                            )}
                        </Price>
                    </PriceRow>
                </PriceGroup>
            ) : null}

            {last_sell ? (
                <PriceGroup>
                    <Label $cardSize={cardSize}>Last sale</Label>
                    <PriceRow>
                        <Price>
                            <Icon $cardSize={cardSize}>{prefix} </Icon>
                            <Value $cardSize={cardSize}>{formatEthValue(last_sell || 0)}</Value>
                        </Price>
                        {last_buy && (
                            <Change>
                                <ArrowChangeLabel
                                    value={getTokenPercentChange(last_buy, last_sell || 0)}
                                    size={cardSize === 'small' ? 12 : 16}
                                />
                            </Change>
                        )}
                    </PriceRow>
                </PriceGroup>
            ) : null}
        </Wrapper>
    );
};
